import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../lib/api';
import { toast } from 'react-toastify';

export interface Photo {
  id: string;
  s3Key: string;
  order: number;
  isPrimary: boolean;
}

interface PhotoUploaderProps {
  photos: Photo[];
  onChange: (photos: Photo[]) => void;
  listingId?: string;
  maxPhotos?: number;
}

interface SortablePhotoProps {
  photo: Photo;
  index: number;
  onSetPrimary: (id: string) => void;
  onDelete: (id: string) => void;
  s3BaseUrl: string;
}

const SortablePhoto: React.FC<SortablePhotoProps> = ({
  photo,
  index,
  onSetPrimary,
  onDelete,
  s3BaseUrl,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const imageUrl = `${s3BaseUrl}/${photo.s3Key}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group bg-gray-100 rounded-lg overflow-hidden aspect-square"
    >
      <img
        src={imageUrl}
        alt={`Upload ${index + 1}`}
        className="w-full h-full object-contain"
      />
      
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 bg-white bg-opacity-90 rounded p-1 cursor-move opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </div>

      {/* Primary Badge */}
      {photo.isPrimary && (
        <div className="absolute top-2 right-2 bg-primary-600 text-white px-2 py-1 rounded text-xs font-semibold">
          Primary
        </div>
      )}

      {/* Action Buttons */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex gap-2">
          {!photo.isPrimary && (
            <button
              type="button"
              onClick={() => onSetPrimary(photo.id)}
              className="flex-1 bg-white text-gray-900 px-2 py-1 rounded text-xs font-medium hover:bg-gray-100 transition-colors"
              title="Set as primary photo"
            >
              Set Primary
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(photo.id)}
            className="flex-1 bg-red-600 text-white px-2 py-1 rounded text-xs font-medium hover:bg-red-700 transition-colors"
            title="Delete photo"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const PhotoUploader: React.FC<PhotoUploaderProps> = ({ photos, onChange, listingId, maxPhotos = 20 }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  const s3BaseUrl = process.env.REACT_APP_S3_BASE_URL || 'https://pay2rentme-images-dev.s3.amazonaws.com';

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (photos.length + acceptedFiles.length > maxPhotos) {
        toast.error(`Maximum ${maxPhotos} photos allowed`);
        return;
      }

      setIsUploading(true);

      try {
        const uploadPromises = acceptedFiles.map(async (file, index) => {
          // Validate file size (10MB)
          if (file.size > 10 * 1024 * 1024) {
            toast.error(`${file.name} exceeds 10MB limit`);
            return null;
          }

          // Get presigned URL
          const { uploadUrl, s3Key } = await api.post<{ uploadUrl: string; s3Key: string }>(
            '/listings/upload-url',
            {
              fileName: file.name,
              fileType: file.type,
              listingId,
            }
          );

          const photoId = `photo-${Date.now()}-${index}`;
          setUploadProgress((prev) => ({ ...prev, [photoId]: 0 }));

          // Upload to S3
          await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          setUploadProgress((prev) => ({ ...prev, [photoId]: 100 }));

          return {
            id: photoId,
            s3Key: s3Key,
            order: photos.length + index,
            isPrimary: false,
          };
        });

        const uploadedPhotos = (await Promise.all(uploadPromises)).filter(
          (p): p is Photo => p !== null
        );

        // If this is the first photo, make it primary
        if (photos.length === 0 && uploadedPhotos.length > 0) {
          uploadedPhotos[0].isPrimary = true;
        }

        onChange([...photos, ...uploadedPhotos]);
        toast.success(`${uploadedPhotos.length} photo(s) uploaded successfully`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload photos');
      } finally {
        setIsUploading(false);
        setUploadProgress({});
      }
    },
    [photos, onChange, listingId, maxPhotos]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: isUploading || photos.length >= maxPhotos,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = photos.findIndex((p) => p.id === active.id);
      const newIndex = photos.findIndex((p) => p.id === over.id);

      const reorderedPhotos = arrayMove(photos, oldIndex, newIndex).map((photo, index) => ({
        ...photo,
        order: index,
      }));

      onChange(reorderedPhotos);
    }
  };

  const handleSetPrimary = (id: string) => {
    const updatedPhotos = photos.map((photo) => ({
      ...photo,
      isPrimary: photo.id === id,
    }));
    onChange(updatedPhotos);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      const updatedPhotos = photos
        .filter((p) => p.id !== id)
        .map((photo, index) => ({
          ...photo,
          order: index,
          // If we're deleting the primary photo, make the first remaining photo primary
          isPrimary: photos.find((p) => p.id === id)?.isPrimary && index === 0 ? true : photo.isPrimary,
        }));
      onChange(updatedPhotos);
      toast.success('Photo deleted');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Photos ({photos.length}/{maxPhotos})
        </label>
        {photos.length > 0 && (
          <span className="text-xs text-gray-500">Drag to reorder</span>
        )}
      </div>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <SortablePhoto
                  key={photo.id}
                  photo={photo}
                  index={index}
                  onSetPrimary={handleSetPrimary}
                  onDelete={handleDelete}
                  s3BaseUrl={s3BaseUrl}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Upload Dropzone */}
      {photos.length < maxPhotos && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary-600 bg-primary-50'
              : 'border-gray-300 hover:border-primary-400'
          } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="space-y-2">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isUploading ? (
              <p className="text-sm text-gray-600">Uploading...</p>
            ) : isDragActive ? (
              <p className="text-sm text-primary-600 font-medium">Drop photos here...</p>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Drag and drop photos here, or click to select
                </p>
                <p className="text-xs text-gray-500">Max 10MB per photo, up to {maxPhotos} photos</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([id, progress]) => (
            <div key={id} className="bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary-600 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          ))}
        </div>
      )}

      {photos.length >= maxPhotos && (
        <p className="text-sm text-yellow-600">
          You've reached the maximum of {maxPhotos} photos
        </p>
      )}
    </div>
  );
};

export default PhotoUploader;

